import { useId } from 'react';

/**
 * The app's one checkbox: a real (visually hidden) <input> for keyboard and
 * screen-reader behaviour, drawn as a styled box + tick so it looks the
 * same in every browser.
 *
 * Usage:
 *   <Checkbox label="Also notify by push" checked={push}
 *             onChange={setPush} hint="Sends a banner to their phone." />
 */

export interface CheckboxProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  hint?: string;
  disabled?: boolean;
  className?: string;
}

export function Checkbox({
  label,
  checked,
  onChange,
  hint,
  disabled,
  className,
}: CheckboxProps) {
  const id = useId();

  return (
    <div className={className}>
      <label
        htmlFor={id}
        className={[
          'flex cursor-pointer items-start gap-2.5',
          disabled ? 'cursor-not-allowed opacity-50' : '',
        ].join(' ')}
      >
        <input
          id={id}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          aria-describedby={hint ? `${id}-hint` : undefined}
          className="peer sr-only"
        />
        {/* The drawn box; peer-focus ring keeps keyboard focus visible. */}
        <span
          aria-hidden="true"
          className={[
            'mt-0.5 grid h-4.5 w-4.5 shrink-0 place-items-center rounded border transition',
            'peer-focus-visible:ring-2 peer-focus-visible:ring-brand-100',
            checked
              ? 'border-brand-600 bg-brand-600'
              : 'border-slate-300 bg-white',
          ].join(' ')}
          style={{ height: '1.125rem', width: '1.125rem' }}
        >
          {checked && (
            <svg viewBox="0 0 20 20" className="h-3 w-3 text-white" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M4 10.5l4 4 8-9" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </span>
        <span className="min-w-0">
          <span className="block text-sm text-slate-700">{label}</span>
          {hint && (
            <span id={`${id}-hint`} className="block text-xs text-slate-500">
              {hint}
            </span>
          )}
        </span>
      </label>
    </div>
  );
}
